/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import PropTypes from "prop-types";
import React from "react";
import { detectComponent, isEmpty } from "../../../utils/utils";
import { AuthorWithTime } from "../../Atoms/AuthorWithTimestamp";
import { CollectionName } from "../../Atoms/CollectionName";
import { Headline } from "../../Atoms/Headline";
import { HeroImage } from "../../Atoms/HeroImage";
import { SectionTag } from "../../Atoms/SectionTag";
import { StoryCard } from "../../Molecules/StoryCard";
import { StateProvider } from "../../SharedContext";
import "./aside-collection.m.css";

const AsideCollection = ({ data = {}, config = {}, horizontal = false, slots = [], adComponent, widgetComp }) => {
  const { theme, title, collectionNameBorderColor, collectionNameTemplate } = config;
  const isCollection = data.type === "collection";
  const storyList = isCollection ? data.items : data;
  const heading = title || data.name || "";
  const isHorizontal = horizontal ? "horizontal-wrapper" : "";
  if (isEmpty(data)) return null;

  const StoryCollection = () => {
    return (
      <>
        <CollectionName
          customCollectionName={heading}
          collectionNameBorderColor={collectionNameBorderColor}
          collectionNameTemplate={collectionNameTemplate}
          textColor
        />
        <div styleName="stories">
          {storyList.length < 0 && (
            <div data-test-id="no-stories" styleName="info">
              No stories found.
            </div>
          )}

          {storyList.map(item => {
            const story = isCollection ? item.story : item;
            return (
              <div styleName="story" key={story.id}>
                <StoryCard story={story} isHorizontal theme={theme}>
                  <div styleName="story-hero-image">
                    <HeroImage story={story} aspectRatio={[[4, 3]]} />
                  </div>
                  <div>
                    {isHorizontal && <SectionTag story={story} />}
                    <Headline story={story} />
                    <div styleName="story-byline">
                      <AuthorWithTime story={story} />
                    </div>
                  </div>
                </StoryCard>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  if (horizontal) {
    return (
      <div
        className="arrow-component arr--aside-collection"
        styleName={`wrapper ${isHorizontal}`}
        style={{ backgroundColor: theme }}
        data-test-id="aside-collection"
      >
        <StoryCollection />
      </div>
    );
  }

  const asideSlot = (type, slot) => {
    switch (type) {
      case "ad":
        return (
          <div styleName="ad-slot" className="aside-collection-ad-slot">
            {detectComponent(slot, adComponent, widgetComp)}
          </div>
        );
      case "widget":
        return (
          <div styleName="ad-slot" className="aside-collection-ad-slot">
            {detectComponent(slot, adComponent, widgetComp)}
          </div>
        );

      case "collection":
        return <StoryCollection />;
    }
  };

  return (
    <div
      className="arrow-component arr--aside-collection"
      styleName={`wrapper ${isHorizontal}`}
      style={{ backgroundColor: theme }}
      data-test-id="aside-collection"
    >
      {slots.map(slot => asideSlot(slot.type, slot))}
    </div>
  );
};

AsideCollection.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  config: PropTypes.object,
  horizontal: PropTypes.bool,
  slots: PropTypes.array,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

export default StateProvider(AsideCollection);
